<template>
  <div class="view view-passwort-reset">
    <div
      class="passwort-reset__background"
      :style="backgroundStyle"
      aria-hidden="true"
    ></div>

    <v-container class="passwort-reset__wrapper d-flex align-center justify-center">
      <v-card class="pa-6 pa-sm-8" width="100%" max-width="480">
        <v-card-title class="px-0 pt-0 align-start">
          <v-icon color="primary" class="mr-3 mt-1">
            mdi-lock-reset
          </v-icon>
          <div>
            <div class="text-h6">
              Passwort zurücksetzen
            </div>
            <div class="text-caption text--secondary font-weight-regular mt-1">
              {{ appTitle }}
            </div>
          </div>
        </v-card-title>

        <v-card-text class="px-0">
          <v-skeleton-loader
            v-if="checking"
            type="article, actions"
          ></v-skeleton-loader>

          <v-alert
            v-else-if="completed"
            type="success"
            text
          >
            Ihr Passwort wurde geändert. Alle bisherigen Anmeldesitzungen dieses
            Kontos wurden aus Sicherheitsgründen beendet.
          </v-alert>

          <div v-else-if="inspectionError">
            <v-alert
              type="error"
              text
            >
              {{ inspectionError }}
            </v-alert>
            <v-btn
              outlined
              color="primary"
              :loading="checking"
              @click="inspectToken"
            >
              Erneut prüfen
            </v-btn>
          </div>

          <v-alert
            v-else-if="!tokenValid"
            type="warning"
            text
          >
            Dieser Link ist ungültig, abgelaufen oder wurde bereits verwendet.
            Fordern Sie bei Bedarf über die Loginseite einen neuen Link an.
          </v-alert>

          <v-form
            v-else
            ref="form"
            v-model="formValid"
            @submit.prevent="submit"
          >
            <p class="text-body-2 mb-5">
              Legen Sie ein neues Passwort mit mindestens 8 Zeichen fest.
              Der Link kann nur einmal verwendet werden.
            </p>
            <v-text-field
              v-model="password"
              label="Neues Passwort"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              autocomplete="new-password"
              maxlength="256"
              :disabled="saving"
              :rules="passwordRules"
              @click:append="showPassword = !showPassword"
            ></v-text-field>
            <v-text-field
              v-model="passwordRepeat"
              label="Neues Passwort wiederholen"
              :type="showPasswordRepeat ? 'text' : 'password'"
              :append-icon="showPasswordRepeat ? 'mdi-eye-off' : 'mdi-eye'"
              autocomplete="new-password"
              maxlength="256"
              :disabled="saving"
              :rules="passwordRepeatRules"
              @click:append="showPasswordRepeat = !showPasswordRepeat"
            ></v-text-field>
            <v-alert
              v-if="message"
              class="mt-3"
              type="error"
              dense
              text
            >
              {{ message }}
            </v-alert>
            <v-btn
              type="submit"
              color="primary"
              block
              class="mt-4"
              :loading="saving"
              :disabled="saving || !formValid"
            >
              Passwort speichern
            </v-btn>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-0 pb-0">
          <v-btn
            text
            color="primary"
            :disabled="saving"
            @click="goToLogin"
          >
            <v-icon left small>
              mdi-arrow-left
            </v-icon>
            Zur Loginseite
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import applyBranding from '@/utils/branding';

function tokenFromLocation() {
  const currentUrl = new URL(window.location.href);
  const hash = currentUrl.hash.replace(/^#/, '');
  const hashParams = new URLSearchParams(hash);
  const token = hashParams.get('token') || currentUrl.searchParams.get('token') || '';

  // Das Bearer-Token sofort aus Adresszeile, Verlauf und möglichen Referrern entfernen.
  currentUrl.hash = '';
  currentUrl.searchParams.delete('token');
  window.history.replaceState(
    window.history.state,
    document.title,
    `${currentUrl.pathname}${currentUrl.search}`,
  );
  return token;
}

export default {
  name: 'PasswortZuruecksetzen',
  data: () => ({
    token: '',
    tokenValid: false,
    checking: true,
    inspectionError: '',
    saving: false,
    completed: false,
    formValid: false,
    password: '',
    passwordRepeat: '',
    showPassword: false,
    showPasswordRepeat: false,
    message: '',
    appTitle: 'OGS digital',
    backgroundColor: '#f5f5f5',
    backgroundImage: '',
  }),
  computed: {
    backgroundStyle() {
      return {
        backgroundColor: this.backgroundColor,
        backgroundImage: this.backgroundImage ? `url('${this.backgroundImage}')` : 'none',
      };
    },
    passwordRules() {
      return [
        (value) => Boolean(value) || 'Bitte ein neues Passwort angeben.',
        (value) => String(value || '').length >= 8 || 'Mindestens 8 Zeichen angeben.',
        (value) => String(value || '').length <= 256 || 'Maximal 256 Zeichen sind erlaubt.',
      ];
    },
    passwordRepeatRules() {
      return [
        (value) => Boolean(value) || 'Bitte das neue Passwort wiederholen.',
        (value) => value === this.password || 'Die Passwörter stimmen nicht überein.',
      ];
    },
  },
  methods: {
    async publicRequest(action, fields = {}) {
      const formData = new FormData();
      formData.append('action', action);
      Object.keys(fields).forEach((field) => formData.append(field, fields[field]));
      const response = await fetch(
        `${process.env.VUE_APP_API_HOST}/login/password_reset.php`,
        {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          referrerPolicy: 'no-referrer',
        },
      );
      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('Die Serverantwort konnte nicht verarbeitet werden.');
      }
      if (!response.ok || result.status !== 'success') {
        throw new Error(result.message || 'Der Link konnte nicht geprüft werden.');
      }
      return result;
    },
    async loadBranding() {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_HOST}/login/get.php?loginscreen`,
          { method: 'POST', credentials: 'same-origin', referrerPolicy: 'no-referrer' },
        );
        const result = await response.json();
        const data = result && result.data ? result.data : null;
        if (!response.ok || !data) return;
        applyBranding(data);
        this.appTitle = data.app_titel || this.appTitle;
        this.backgroundColor = data.loginscreen_bg_color || this.backgroundColor;
        this.backgroundImage = data.loginscreen_bg_img || '';
        if (data.app_theme_color_primary) {
          this.$vuetify.theme.themes.light.primary = data.app_theme_color_primary;
        }
        document.title = `Passwort zurücksetzen – ${this.appTitle}`;
      } catch {
        // Branding ist optional; das sicherheitsrelevante Formular bleibt nutzbar.
      }
    },
    async inspectToken() {
      this.inspectionError = '';
      if (!this.token || this.token.length > 256) {
        this.checking = false;
        return;
      }
      this.checking = true;
      try {
        const result = await this.publicRequest('inspect', { token: this.token });
        this.tokenValid = result.valid === true;
      } catch {
        this.tokenValid = false;
        this.inspectionError = 'Der Server konnte nicht erreicht werden. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.';
      } finally {
        this.checking = false;
      }
    },
    async submit() {
      if (this.saving || !this.$refs.form || !this.$refs.form.validate()) return;
      this.saving = true;
      this.message = '';
      try {
        await this.publicRequest('complete', {
          token: this.token,
          password: this.password,
        });
        this.completed = true;
        this.tokenValid = false;
        this.token = '';
        this.password = '';
        this.passwordRepeat = '';
        // Der Server hat alle Sitzungen beendet. Deshalb auch sämtliche
        // personenbezogenen Offline-Caches verwerfen und den Vuex-Zustand
        // durch einen vollständigen Neustart der Loginseite ersetzen.
        localStorage.clear();
        this.$store.commit('main/mainmenu_switchNav', false);
        const loginRoute = this.$router.resolve({
          path: '/',
          query: { passwordReset: 'success' },
        });
        window.location.replace(loginRoute.href);
      } catch (error) {
        this.message = error.message
          || 'Das Passwort konnte nicht geändert werden. Fordern Sie einen neuen Link an.';
      } finally {
        this.saving = false;
      }
    },
    goToLogin() {
      this.token = '';
      // Ein voller Neustart rehydriert eine eventuell noch vorhandene Sitzung.
      // Ein reiner SPA-Wechsel wuerde nach dem bewusst oeffentlichen Reset-View
      // sonst einen unvollstaendigen Vuex-Zustand zuruecklassen.
      const loginRoute = this.$router.resolve({ path: '/' });
      window.location.replace(loginRoute.href);
    },
  },
  created() {
    this.token = tokenFromLocation();
    this.loadBranding();
    this.inspectToken();
  },
  beforeDestroy() {
    this.token = '';
    this.password = '';
    this.passwordRepeat = '';
  },
};
</script>

<style scoped>
.view-passwort-reset {
  position: fixed;
  inset: 0;
  overflow-y: auto;
}

.passwort-reset__background {
  position: fixed;
  inset: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  transform: scale(1.04);
  z-index: 0;
}

.passwort-reset__background::after {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
  content: '';
}

.passwort-reset__wrapper {
  position: relative;
  min-height: 100vh;
  z-index: 1;
}
</style>
